/*
  PDP REDROOT — galeria, quantidade e compra.

  A compra usa o mesmo caminho da PDP da Hidrogel (pdp-hidrogel.js):
  POST em /cart/add.js, evento `add_to_cart` no dataLayer e a gaveta
  do carrinho do tema redesenhada pela Section Rendering API.

  Sem JavaScript o formulário continua funcionando: ele é o `product`
  nativo da Shopify e envia para /cart/add.
*/

class PdpRedroot {
  constructor(container) {
    this.container = container;
    this.dados = JSON.parse(container.querySelector("[data-pdp-redroot-json]").textContent);
    this.form = container.querySelector("[data-form-compra]");
    this.botao = container.querySelector("[data-botao-compra]");
    this.rotulo = container.querySelector("[data-botao-rotulo]");
    this.qtd = container.querySelector("[data-qtd]");

    // Barra fixa: mesma quantidade e mesmo "adicionar" do bloco de compra
    this.barra = container.querySelector("[data-barra]");
    this.barraQtd = container.querySelector("[data-barra-qtd]");
    this.barraBotao = container.querySelector("[data-barra-comprar]");
    this.barraRotulo = container.querySelector("[data-barra-rotulo]");
    this.barraTotal = container.querySelector("[data-barra-total]");
    this.barraUnidade = container.querySelector("[data-barra-unidade]");
    this.moeda = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

    this.bindGaleria();
    this.bindQuantidade();
    this.bindCompra();
    this.bindBarra();
    this.bindOpcoes();
  }

  // Produto com variantes (roupas): escolher tamanho/cor troca o item
  // do carrinho, o preço, a disponibilidade e a foto da variante.
  bindOpcoes() {
    const caixa = this.container.querySelector("[data-opcoes]");
    const variantes = this.dados.variantes || [];
    if (!caixa || variantes.length < 2) return;
    this.precoEl = this.container.querySelector("[data-preco]");
    this.inputId = this.form ? this.form.querySelector('input[name="id"]') : null;
    caixa.addEventListener("change", () => this.trocarVariante(caixa, variantes));
    this.marcarEsgotados(caixa, variantes);
  }

  escolhidas(caixa) {
    const valores = [];
    caixa.querySelectorAll("input[data-opcao]:checked").forEach((r) => { valores[Number(r.dataset.opcao)] = r.value; });
    return valores;
  }

  trocarVariante(caixa, variantes) {
    const valores = this.escolhidas(caixa);
    valores.forEach((v, i) => {
      const alvo = caixa.querySelector(`[data-opcao-escolhida="${i}"]`);
      if (alvo) alvo.textContent = v;
    });
    const variante = variantes.find((v) => v.opcoes.every((o, i) => o === valores[i]));
    this.marcarEsgotados(caixa, variantes);

    if (!variante) {
      this.dados.indisponivel = true;
      this.estado("Indisponível", true);
      return;
    }

    this.dados.variantId = variante.id;
    this.dados.precoCentavos = variante.preco;
    this.dados.indisponivel = !variante.disponivel;
    if (this.inputId) this.inputId.value = variante.id;

    if (this.precoEl) {
      const de = variante.precoDe > variante.preco ? `<s class="rr__preco-de">${this.moeda.format(variante.precoDe / 100)}</s>` : "";
      this.precoEl.innerHTML = `${de}<span>${this.moeda.format(variante.preco / 100)}</span>`;
    }
    this.definirQuantidade(this.quantidade());
    this.estado(variante.disponivel ? this.dados.rotulo : "Esgotado", !variante.disponivel);

    if (variante.foto) {
      const principal = this.container.querySelector(".rr__principal img");
      if (principal) {
        principal.removeAttribute("srcset");
        principal.src = variante.foto;
      }
    }

    const url = new URL(window.location.href);
    url.searchParams.set("variant", variante.id);
    window.history.replaceState({}, "", url);
  }

  // Risca os valores que, combinados com as outras escolhas, estão esgotados
  marcarEsgotados(caixa, variantes) {
    const valores = this.escolhidas(caixa);
    caixa.querySelectorAll("input[data-opcao]").forEach((r) => {
      const i = Number(r.dataset.opcao);
      const teste = valores.slice();
      teste[i] = r.value;
      const ok = variantes.some((v) => v.disponivel && v.opcoes.every((o, k) => o === teste[k]));
      r.closest(".rr__chip").classList.toggle("is-esgotado", !ok);
    });
  }

  bindGaleria() {
    const principal = this.container.querySelector(".rr__principal img");
    const minis = this.container.querySelectorAll("[data-miniaturas] button");
    if (!principal || minis.length === 0) return;

    minis.forEach((mini) => {
      mini.addEventListener("click", () => {
        principal.src = mini.dataset.src;
        principal.srcset = mini.dataset.srcset;
        principal.alt = mini.dataset.alt;
        minis.forEach((m) => m.setAttribute("aria-pressed", m === mini ? "true" : "false"));
      });
    });
  }

  quantidade() {
    const n = parseInt(this.qtd.value, 10);
    return Number.isFinite(n) ? Math.min(99, Math.max(1, n)) : 1;
  }

  // Uma quantidade só: escrever aqui atualiza o bloco, a barra e o total.
  definirQuantidade(valor) {
    const n = Number.isFinite(valor) ? Math.min(99, Math.max(1, valor)) : 1;
    if (this.qtd) this.qtd.value = n;
    if (this.barraQtd) this.barraQtd.value = n;
    if (this.barraTotal) this.barraTotal.textContent = this.moeda.format((this.dados.precoCentavos * n) / 100);
    if (this.barraUnidade) this.barraUnidade.hidden = n === 1;
  }

  ligarSeletor(input, menos, mais) {
    if (!input) return;
    menos.addEventListener("click", () => this.definirQuantidade(this.quantidade() - 1));
    mais.addEventListener("click", () => this.definirQuantidade(this.quantidade() + 1));
    input.addEventListener("change", () => this.definirQuantidade(parseInt(input.value, 10)));
  }

  bindQuantidade() {
    const c = this.container;
    this.ligarSeletor(this.qtd, c.querySelector("[data-qtd-menos]"), c.querySelector("[data-qtd-mais]"));
    this.ligarSeletor(this.barraQtd, c.querySelector("[data-barra-menos]"), c.querySelector("[data-barra-mais]"));
  }

  bindCompra() {
    if (!this.form || !this.botao) return;
    this.form.addEventListener("submit", (evento) => {
      evento.preventDefault();
      this.adicionar();
    });
    if (this.barraBotao) this.barraBotao.addEventListener("click", () => this.adicionar());
  }

  // A barra aparece quando o botão principal sai da tela (acima ou
  // abaixo) e some quando ele volta — nunca dois botões à vista.
  // Enquanto visível, reserva no fim da página a altura dela, para
  // não cobrir o rodapé.
  bindBarra() {
    if (!this.barra || !this.form || !("IntersectionObserver" in window)) return;
    const mostrar = (visivel) => {
      this.barra.classList.toggle("is-visivel", visivel);
      this.barra.setAttribute("aria-hidden", visivel ? "false" : "true");
      this.barra.inert = !visivel;
      document.body.style.paddingBottom = visivel ? `${this.barra.offsetHeight}px` : "";
    };
    new IntersectionObserver(([entrada]) => mostrar(!entrada.isIntersecting), { threshold: 0 }).observe(this.form);
  }

  pushEvent(nome, dados) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: nome, ...dados });
  }

  // Botão e rótulo do bloco e da barra andam juntos
  estado(texto, desabilitado) {
    [this.botao, this.barraBotao].forEach((b) => { if (b) b.disabled = desabilitado; });
    [this.rotulo, this.barraRotulo].forEach((r) => { if (r) r.textContent = texto; });
  }

  async adicionar() {
    if (this.botao.disabled) return;
    const quantidade = this.quantidade();
    this.estado("Adicionando...", true);

    try {
      const resposta = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: this.dados.variantId, quantity: quantidade }] }),
      });
      if (!resposta.ok) throw new Error("Falha ao adicionar ao carrinho");

      const preco = this.dados.precoCentavos / 100;
      this.pushEvent("add_to_cart", {
        ecommerce: {
          currency: "BRL",
          value: preco * quantidade,
          items: [{ item_id: String(this.dados.variantId), item_name: this.dados.nome, price: preco, quantity: quantidade }],
        },
      });

      await this.atualizarGaveta();
      this.estado("✓ Adicionado!", true);
    } catch (erro) {
      console.error("pdp-redroot: erro ao adicionar", erro);
      this.estado("Erro ao adicionar", true);
    } finally {
      setTimeout(() => {
        if (this.dados.indisponivel) this.estado("Esgotado", true);
        else this.estado(this.dados.rotulo, false);
      }, 2000);
    }
  }

  async atualizarGaveta() {
    const gaveta = document.querySelector("cart-drawer-component");
    if (!gaveta) return;

    const itens = gaveta.querySelector(".cart-items-component");
    const sectionId = itens ? itens.dataset.sectionId : null;
    if (!sectionId) return;

    const html = await (await fetch(`/?section_id=${sectionId}`)).text();
    const novo = new DOMParser().parseFromString(html, "text/html");

    const novosItens = novo.querySelector(".cart-items-component");
    if (novosItens) itens.innerHTML = novosItens.innerHTML;

    const novaBolha = novo.querySelector(".header-actions__action .cart-bubble");
    const bolha = document.querySelector(".header-actions__action .cart-bubble");
    if (bolha && novaBolha) {
      bolha.innerHTML = novaBolha.innerHTML;
      if (novaBolha.dataset.cartCount !== undefined) bolha.setAttribute("data-cart-count", novaBolha.dataset.cartCount);
    }

    const carrinho = await (await fetch("/cart.js")).json();
    const modal = gaveta.querySelector(".dialog-modal");
    if (modal) modal.classList.toggle("cart-drawer--empty", carrinho.item_count === 0);

    if (typeof gaveta.open === "function") gaveta.open();
  }
}

function iniciarPdpRedroot() {
  document.querySelectorAll("[data-pdp-redroot]:not(.js-iniciado)").forEach((container) => {
    container.classList.add("js-iniciado");
    new PdpRedroot(container);
  });
}

document.addEventListener("DOMContentLoaded", iniciarPdpRedroot);
document.addEventListener("shopify:section:load", iniciarPdpRedroot);

